/**
 * Calendar days and months as the API writes them ("2026-11-05", "2026-11"):
 * checked, moved and laid out in weeks, always counted in UTC, so the
 * browser's time zone can never move a day. For the day pickers
 * (`<DayPicker>`) and the month choices.
 */

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether a text is a month as the API writes it: "2026-11". */
export function isMonth(value: unknown): value is string {
  return typeof value === "string" && MONTH.test(value);
}

/** A month moved by a number of months: "2026-12" and 1 give "2027-01". */
export function shiftMonth(month: string, by: number): string {
  const match = MONTH.exec(month);
  if (!match) {
    return month;
  }
  const index = Number(match[1]) * 12 + Number(match[2]) - 1 + by;
  const year = Math.floor(index / 12);
  return `${String(year).padStart(4, "0")}-${String(index - year * 12 + 1).padStart(2, "0")}`;
}

/** A calendar day as a moment at midnight UTC, so no time zone moves it; null for no day. */
function utcDate(day: string): Date | null {
  const match = DAY.exec(day);
  if (!match) {
    return null;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  // A day the calendar lacks ("2026-02-30") would roll over into the next month.
  return Number.isNaN(date.getTime()) || dayOf(date) !== day ? null : date;
}

/** A moment at midnight UTC as a calendar day: "2026-11-05". */
function dayOf(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Whether a text is a calendar day as the API writes it: "2026-11-05". */
export function isDay(value: unknown): value is string {
  return typeof value === "string" && utcDate(value) !== null;
}

/** A calendar day moved by a number of days: "2026-11-30" and 1 give "2026-12-01". */
export function shiftDay(day: string, by: number): string {
  const date = utcDate(day);
  if (!date) {
    return day;
  }
  date.setUTCDate(date.getUTCDate() + by);
  return dayOf(date);
}

/** How many days lie between two calendar days: 1 from "2026-11-30" to "2026-12-01"; null for no day. */
export function daysBetween(from: string, to: string): number | null {
  const start = utcDate(from);
  const end = utcDate(to);
  return start && end ? Math.round((end.getTime() - start.getTime()) / 86_400_000) : null;
}

/**
 * A month's calendar: its weeks from Monday to Sunday, each day as
 * "2026-11-05", with null for the days of the weeks that fall in the month
 * before or after.
 */
export function calendarWeeks(month: string): (string | null)[][] {
  const first = utcDate(`${month}-01`);
  if (!first || !isMonth(month)) {
    return [];
  }
  // Monday is 0, Sunday 6.
  const lead = (first.getUTCDay() + 6) % 7;
  const length = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length }, (_, index) => `${month}-${String(index + 1).padStart(2, "0")}`),
  ];
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return Array.from({ length: cells.length / 7 }, (_, week) => cells.slice(week * 7, week * 7 + 7));
}
